'use client';

import React from 'react';
import type { HospitalCardProps } from './types';
import { pickLocaleName, formatPrice } from './utils';
import { MAX_SPECIALTY_TAGS } from './constants';
import { HotRibbonV2 } from './HotRibbonV2';
import { BestRibbonV2 } from './BestRibbonV2';
import { MedicalSpecialtyTagV2 } from './MedicalSpecialtyTagV2';
import { StarIconV2 } from './StarIconV2';
import { ThumbnailPlaceholder } from './ThumbnailPlaceholder';
import { useI18n } from '@/shared/providers/I18nProvider';

export function HospitalCard({ h }: HospitalCardProps) {
  const { dict, lang } = useI18n();
  const name = pickLocaleName(h.name, lang);
  const location = pickLocaleName(h.location, lang);
  const price = formatPrice(h.minPriceUsd);
  const [imgError, setImgError] = React.useState(false);
  const showImage = h.thumbnailImageUrl && !imgError;
  const firstBadge = h.badge?.[0];
  const specialtyTags = (h.medicalSpecialties ?? []).slice(0, MAX_SPECIALTY_TAGS);
  const href = `https://www.k-doc.kr/${lang}/hospital/${h.id}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="relative block"
    >
      {firstBadge === 'HOT' && <HotRibbonV2 />}
      {firstBadge === 'BEST' && <BestRibbonV2 />}

      <div className="flex flex-col items-center overflow-clip rounded-xl bg-white shadow-[1px_2px_4px_0_rgba(0,0,0,0.40)]">
        <div className="relative w-full shrink-0 overflow-clip aspect-[1.15]">
          {showImage ? (
            <img
              src={h.thumbnailImageUrl!}
              alt={name}
              className="h-full w-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <ThumbnailPlaceholder />
          )}
        </div>

        <div className="flex h-[187px] w-full shrink-0 flex-col items-start gap-1 p-3">
          <div className="flex w-full shrink-0 flex-col items-start gap-1.5">
            {specialtyTags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {specialtyTags.map((s) => (
                  <MedicalSpecialtyTagV2
                    key={s.id}
                    name={pickLocaleName(s.name, lang)}
                  />
                ))}
              </div>
            )}
            <p className="relative line-clamp-2 w-full shrink-0 text-sm font-semibold leading-5 text-neutral-700">
              {name}
            </p>
            {location ? (
              <div className="flex w-full min-w-0 shrink-0 items-center gap-1 overflow-hidden">
                <p className="relative shrink-0 text-xs font-medium leading-4 text-neutral-400">
                  {dict.chat.hospital.regionLabel}
                </p>
                <div className="relative flex h-[10px] w-0 shrink-0 items-center justify-center">
                  <div className="flex-none rotate-90">
                    <svg
                      width="10"
                      height="1"
                      viewBox="0 0 10 1"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute inset-0"
                    >
                      <line y1="0.5" x2="10" y2="0.5" stroke="#A3A3A3" />
                    </svg>
                  </div>
                </div>
                <div className="relative flex min-w-0 flex-1 items-center overflow-hidden py-0 pr-0">
                  <p className="relative min-w-0 shrink truncate text-xs font-medium leading-4 text-neutral-400">
                    {location}
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex w-full shrink-0 flex-col items-start gap-0.5">
            <p className="relative min-w-full shrink-0 whitespace-pre-wrap text-sm font-semibold leading-7 text-neutral-700">
              {price}
            </p>
            <div className="flex shrink-0 items-center gap-1">
              <div className="flex size-4 shrink-0 items-center justify-center">
                <StarIconV2 />
              </div>
              <div className="flex shrink-0 items-center gap-0.5 text-[13px] font-medium leading-[19px]">
                <span className="relative shrink-0 text-neutral-700">
                  {h.rating.toFixed(1)}
                </span>
                <span className="relative shrink-0 text-neutral-400">
                  ({h.reviewCount})
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}
